// src/pages/fiscal/FinancialLedgerPage.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { expenseService } from '../../services/expense.service';
import { incomeService } from '../../services/income.service';
import { userPaymentService } from '../../services/userPayment.service';
import { logisticsService } from '../../services/logistics.service';
import { invoiceService } from '../../services/invoice.service';
import { Expense, Invoice, UserPayment, CreateIncomeDto } from '../../types/finance.types';
import { ProjectLogistics } from '../../types/logistics.types';
import { format, startOfYear, endOfYear, getYear } from 'date-fns';
import { formatSafeDate } from '../../utils/dateUtils';
import {
  DollarSign,
  Download,
  TrendingDown,
  TrendingUp,
  FileText,
  Search,
  Filter,
  Truck,
  Users,
  Receipt,
  CreditCard,
  Clock,
  CheckCircle,
  AlertCircle,
  Trash2,
  X
} from 'lucide-react';
import { toast } from 'react-hot-toast';

// Unified ledger entry type for expenses
interface ExpenseLedgerEntry {
  id: string;
  date: Date;
  description: string;
  category: string;
  projectId?: string;
  projectName?: string;
  amount: number;
  source: 'expense' | 'staff_payment' | 'logistics';
  sourceId: string;
}

// Income ledger entry type
interface IncomeLedgerEntry {
  id: string;
  date: Date;
  invoiceNumber: string;
  clientName: string;
  projectId: string;
  projectName: string;
  amount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'received' | 'expected';
  source: 'invoice' | 'manual';
  category?: string; // for manual income (Consulting, Grant, …)
}

type TabType = 'expenses' | 'income';

const FinancialLedgerPage: React.FC = () => {
  const { userProfile } = useAuth();
  const { projects, selectedProjectId } = useProject();

  const [activeTab, setActiveTab] = useState<TabType>('expenses');
  const [loading, setLoading] = useState(true);

  // Data
  const [expenseEntries, setExpenseEntries] = useState<ExpenseLedgerEntry[]>([]);
  const [incomeEntries, setIncomeEntries] = useState<IncomeLedgerEntry[]>([]);

  // Filters
  const [selectedYear, setSelectedYear] = useState(getYear(new Date()));
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Add-income modal (provider_admin only — this whole page is gated)
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [savingIncome, setSavingIncome] = useState(false);
  const emptyIncome = (): CreateIncomeDto => ({
    date: new Date(), amount: 0, category: 'Consulting', description: '',
    status: 'received', paymentMethod: 'bank_transfer', projectId: '', projectName: '',
    clientName: '', notes: '',
  });
  const [incomeForm, setIncomeForm] = useState<CreateIncomeDto>(emptyIncome());

  const handleCreateIncome = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile?.uid) return;
    if (!incomeForm.amount || incomeForm.amount <= 0 || !incomeForm.description.trim()) {
      toast.error('Enter an amount and description.');
      return;
    }
    setSavingIncome(true);
    try {
      const proj = projects.find(p => p.id === incomeForm.projectId);
      await incomeService.createIncome({ ...incomeForm, projectName: proj?.name || '' }, userProfile.uid);
      toast.success('Income recorded');
      setShowIncomeModal(false);
      setIncomeForm(emptyIncome());
      loadData();
    } catch (err) {
      console.error('Error creating income:', err);
      toast.error('Failed to record income');
    } finally {
      setSavingIncome(false);
    }
  };

  const handleDeleteIncome = async (id: string) => {
    if (!window.confirm('Delete this income record?')) return;
    try {
      await incomeService.deleteIncome(id);
      toast.success('Income deleted');
      loadData();
    } catch (err) {
      console.error('Error deleting income:', err);
      toast.error('Failed to delete income');
    }
  };

  const canViewLedger = userProfile?.role === 'provider_admin';
  const currentYear = getYear(new Date());
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    if (canViewLedger) {
      loadData();
    }
  }, [selectedYear, canViewLedger]);

  const loadData = async () => {
    setLoading(true);
    try {
      const yearStart = startOfYear(new Date(selectedYear, 0, 1));
      const yearEnd = endOfYear(new Date(selectedYear, 0, 1));

      // Load all data sources in parallel
      const [expenses, payments, logistics, invoices] = await Promise.all([
        expenseService.getExpenses({ startDate: yearStart, endDate: yearEnd }),
        userPaymentService.getUserPayments({ startDate: yearStart, endDate: yearEnd }),
        logisticsService.getLogisticsList(),
        invoiceService.getInvoices({ startDate: yearStart, endDate: yearEnd })
      ]);

      // Transform expenses into ledger entries
      const expenseLedger: ExpenseLedgerEntry[] = expenses.map(exp => ({
        id: exp.id,
        date: exp.date.toDate(),
        description: exp.description,
        category: exp.categoryName,
        projectId: exp.projectId,
        projectName: exp.projectName,
        amount: exp.amount,
        source: 'expense' as const,
        sourceId: exp.id
      }));

      // Transform paid staff payments into ledger entries
      const paidPayments = payments.filter(p => p.status === 'paid');
      const paymentLedger: ExpenseLedgerEntry[] = paidPayments.map(pmt => ({
        id: pmt.id,
        date: pmt.paidDate?.toDate() || pmt.createdAt.toDate(),
        description: `Staff Payment: ${pmt.userName} - ${pmt.description}`,
        category: 'Staff Payments',
        projectId: pmt.projectId,
        projectName: pmt.projectName,
        amount: pmt.amount,
        source: 'staff_payment' as const,
        sourceId: pmt.id
      }));

      // Transform logistics with costs into ledger entries
      const logisticsWithCosts = logistics.filter(l => l.totalCost && l.totalCost > 0);
      const logisticsLedger: ExpenseLedgerEntry[] = logisticsWithCosts.map(log => {
        // Determine the date based on logistics type
        let logDate = log.createdAt.toDate();
        if (log.type === 'hotel' && log.hotel?.checkInDate) {
          logDate = new Date(log.hotel.checkInDate + 'T12:00:00');
        } else if (log.type === 'flight' && log.flight?.departureDate) {
          logDate = new Date(log.flight.departureDate + 'T12:00:00');
        } else if (log.type === 'rental_car' && log.rentalCar?.pickupDate) {
          logDate = new Date(log.rentalCar.pickupDate + 'T12:00:00');
        } else if (log.type === 'ground_transport' && log.groundTransport?.pickupDate) {
          logDate = new Date(log.groundTransport.pickupDate + 'T12:00:00');
        }

        // Get logistics type label
        const typeLabels: Record<string, string> = {
          hotel: 'Lodging',
          flight: 'Flights',
          rental_car: 'Car Rental',
          ground_transport: 'Ground Transport',
          other: 'Other Travel'
        };

        return {
          id: log.id,
          date: logDate,
          description: `${typeLabels[log.type] || 'Logistics'}: ${log.title || log.description || 'Travel expense'}`,
          category: typeLabels[log.type] || 'Travel',
          projectId: log.projectId,
          projectName: log.projectName,
          amount: log.totalCost!,
          source: 'logistics' as const,
          sourceId: log.id
        };
      }).filter(entry => entry.date >= yearStart && entry.date <= yearEnd);

      // Combine all expense entries
      const allExpenses = [...expenseLedger, ...paymentLedger, ...logisticsLedger];
      // Sort by date descending
      allExpenses.sort((a, b) => b.date.getTime() - a.date.getTime());
      setExpenseEntries(allExpenses);

      // Transform invoices into income ledger entries
      const incomeLedger: IncomeLedgerEntry[] = invoices.map(inv => ({
        id: inv.id,
        date: inv.issueDate.toDate(),
        invoiceNumber: inv.invoiceNumber,
        clientName: inv.clientName,
        projectId: inv.projectId,
        projectName: inv.projectName,
        amount: inv.total,
        status: inv.status,
        source: 'invoice' as const,
      }));

      // Manual (non-invoice) income records.
      const manualIncome = await incomeService.getIncome({ startDate: yearStart, endDate: yearEnd });
      const manualLedger: IncomeLedgerEntry[] = manualIncome.map(inc => ({
        id: inc.id,
        date: inc.date.toDate(),
        invoiceNumber: '—',
        clientName: inc.clientName || '—',
        projectId: inc.projectId || '',
        projectName: inc.projectName || 'General',
        amount: inc.amount,
        status: inc.status,
        source: 'manual' as const,
        category: inc.category,
      }));

      const allIncome = [...incomeLedger, ...manualLedger];
      // Sort by date descending
      allIncome.sort((a, b) => b.date.getTime() - a.date.getTime());
      setIncomeEntries(allIncome);

    } catch (error) {
      console.error('Error loading ledger data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  // Filter expense entries
  const filteredExpenses = expenseEntries.filter(entry => {
    if (projectFilter !== 'all' && entry.projectId !== projectFilter) return false;
    if (categoryFilter !== 'all' && entry.category !== categoryFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        entry.description.toLowerCase().includes(query) ||
        entry.category.toLowerCase().includes(query) ||
        entry.projectName?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Filter income entries
  const filteredIncome = incomeEntries.filter(entry => {
    if (projectFilter !== 'all' && entry.projectId !== projectFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        entry.invoiceNumber.toLowerCase().includes(query) ||
        entry.clientName.toLowerCase().includes(query) ||
        entry.projectName.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Calculate totals
  const expenseTotal = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
  const incomeTotal = filteredIncome.filter(i => i.status === 'paid' || i.status === 'received').reduce((sum, i) => sum + i.amount, 0);
  const receivablesTotal = filteredIncome.filter(i => i.status === 'sent' || i.status === 'overdue').reduce((sum, i) => sum + i.amount, 0);
  const overdueTotal = filteredIncome.filter(i => i.status === 'overdue').reduce((sum, i) => sum + i.amount, 0);

  // Get unique categories for filter
  const uniqueCategories = Array.from(new Set(expenseEntries.map(e => e.category))).sort();

  // Export to CSV
  const exportToCSV = () => {
    let csvContent = '';
    let fileName = '';

    if (activeTab === 'expenses') {
      csvContent = 'Date,Description,Category,Project,Amount,Source\n';
      filteredExpenses.forEach(entry => {
        csvContent += `"${format(entry.date, 'yyyy-MM-dd')}","${entry.description.replace(/"/g, '""')}","${entry.category}","${entry.projectName || 'N/A'}","${entry.amount.toFixed(2)}","${entry.source}"\n`;
      });
      fileName = `expenses_ledger_${selectedYear}.csv`;
    } else {
      csvContent = 'Date,Invoice #,Client,Project,Category,Amount,Status,Source\n';
      filteredIncome.forEach(entry => {
        csvContent += `"${format(entry.date, 'yyyy-MM-dd')}","${entry.invoiceNumber}","${entry.clientName.replace(/"/g, '""')}","${entry.projectName}","${entry.category || ''}","${entry.amount.toFixed(2)}","${entry.status}","${entry.source}"\n`;
      });
      fileName = `income_ledger_${selectedYear}.csv`;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('CSV exported successfully');
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'expense':
        return <Receipt className="h-4 w-4 text-gray-500" />;
      case 'staff_payment':
        return <Users className="h-4 w-4 text-orange-500" />;
      case 'logistics':
        return <Truck className="h-4 w-4 text-blue-500" />;
      default:
        return <CreditCard className="h-4 w-4 text-gray-500" />;
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'expense':
        return 'Expense';
      case 'staff_payment':
        return 'Staff Payment';
      case 'logistics':
        return 'Logistics';
      default:
        return 'Other';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'received':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="h-3 w-3" />
            Received
          </span>
        );
      case 'expected':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
            <Clock className="h-3 w-3" />
            Expected
          </span>
        );
      case 'paid':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="h-3 w-3" />
            Paid
          </span>
        );
      case 'sent':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
            <Clock className="h-3 w-3" />
            Sent
          </span>
        );
      case 'overdue':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <AlertCircle className="h-3 w-3" />
            Overdue
          </span>
        );
      case 'draft':
        return (
          <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            Draft
          </span>
        );
      case 'cancelled':
        return (
          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs font-medium line-through">
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  if (!canViewLedger) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
          <p className="text-gray-600 mt-2">You don't have permission to view the financial ledger.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Financial Ledger</h1>
            <p className="mt-1 text-sm text-gray-600">
              Unified view of all expenses and income
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setIncomeForm(emptyIncome()); setShowIncomeModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <DollarSign className="h-4 w-4" />
              Add income
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('expenses')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'expenses'
                ? 'bg-red-100 text-red-700 border-2 border-red-300'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            <TrendingDown className="h-4 w-4" />
            All Expenses
          </button>
          <button
            onClick={() => setActiveTab('income')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'income'
                ? 'bg-green-100 text-green-700 border-2 border-green-300'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            <TrendingUp className="h-4 w-4" />
            All Income
          </button>
        </div>

        {/* Summary Cards */}
        {activeTab === 'expenses' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-red-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-red-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-1">Total Expenses</p>
              <p className="text-2xl font-bold text-red-600">${expenseTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-gray-100 rounded-lg">
                  <Receipt className="h-5 w-5 text-gray-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-1">Regular Expenses</p>
              <p className="text-2xl font-bold text-gray-900">
                ${filteredExpenses.filter(e => e.source === 'expense').reduce((s, e) => s + e.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-orange-100 rounded-lg">
                  <Users className="h-5 w-5 text-orange-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-1">Staff Payments</p>
              <p className="text-2xl font-bold text-orange-600">
                ${filteredExpenses.filter(e => e.source === 'staff_payment').reduce((s, e) => s + e.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-blue-100 rounded-lg">
                  <Truck className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-1">Logistics/Travel</p>
              <p className="text-2xl font-bold text-blue-600">
                ${filteredExpenses.filter(e => e.source === 'logistics').reduce((s, e) => s + e.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-green-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-1">Total Paid</p>
              <p className="text-2xl font-bold text-green-600">${incomeTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-blue-100 rounded-lg">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-1">Receivables</p>
              <p className="text-2xl font-bold text-blue-600">${receivablesTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-red-100 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-1">Overdue</p>
              <p className="text-2xl font-bold text-red-600">${overdueTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-purple-100 rounded-lg">
                  <FileText className="h-5 w-5 text-purple-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-1">Total Invoices</p>
              <p className="text-2xl font-bold text-gray-900">{filteredIncome.length}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={activeTab === 'expenses' ? 'Search expenses...' : 'Search invoices...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-md"
                />
              </div>
            </div>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 border rounded-md bg-white"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="px-3 py-2 border rounded-md bg-white"
            >
              <option value="all">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>

            {activeTab === 'expenses' && (
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border rounded-md bg-white"
              >
                <option value="all">All Categories</option>
                {uniqueCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : activeTab === 'expenses' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredExpenses.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-gray-500">
                        <Receipt className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p>No expenses found for the selected filters</p>
                      </td>
                    </tr>
                  ) : (
                    filteredExpenses.map(entry => (
                      <tr key={`${entry.source}-${entry.id}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {format(entry.date, 'MMM d, yyyy')}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">{entry.description}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                            {entry.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {entry.projectName || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-red-600">
                          -${entry.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getSourceIcon(entry.source)}
                            <span className="text-xs text-gray-500">{getSourceLabel(entry.source)}</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {filteredExpenses.length > 0 && (
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-sm font-semibold text-gray-900">
                        Total ({filteredExpenses.length} entries)
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-red-600">
                        -${expenseTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client / Project</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredIncome.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p>No invoices found for the selected filters</p>
                      </td>
                    </tr>
                  ) : (
                    filteredIncome.map(entry => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {format(entry.date, 'MMM d, yyyy')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {entry.source === 'manual' ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="px-1.5 py-0.5 text-[10px] bg-green-50 text-green-700 rounded">Manual</span>
                              {entry.category}
                            </span>
                          ) : entry.invoiceNumber}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{entry.clientName}</div>
                          <div className="text-xs text-gray-500">{entry.projectName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-green-600">
                          ${entry.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getStatusBadge(entry.status)}
                            {entry.source === 'manual' && (
                              <button
                                onClick={() => handleDeleteIncome(entry.id)}
                                className="text-gray-400 hover:text-red-600"
                                title="Delete income record"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {filteredIncome.length > 0 && (
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={3} className="px-6 py-4 text-sm font-semibold text-gray-900">
                        Total received ({filteredIncome.filter(i => i.status === 'paid' || i.status === 'received').length} entries — paid invoices + manual income)
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-green-600">
                        ${incomeTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add income modal (provider_admin only — page is gated) */}
      {showIncomeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowIncomeModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Record income</h2>
              <button onClick={() => setShowIncomeModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleCreateIncome} className="p-5 space-y-4">
              <p className="text-xs text-gray-500">For non-invoice income (consulting, grants, misc). Invoiced revenue is tracked automatically — don't re-enter it here.</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input type="date" required
                    value={format(incomeForm.date, 'yyyy-MM-dd')}
                    onChange={(e) => setIncomeForm({ ...incomeForm, date: new Date(e.target.value + 'T12:00:00') })}
                    className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                  <input type="number" min="0" step="0.01" required
                    value={incomeForm.amount || ''}
                    onChange={(e) => setIncomeForm({ ...incomeForm, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-md" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input type="text" required
                  value={incomeForm.description}
                  onChange={(e) => setIncomeForm({ ...incomeForm, description: e.target.value })}
                  placeholder="e.g. Consulting retainer — June"
                  className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={incomeForm.category}
                    onChange={(e) => setIncomeForm({ ...incomeForm, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md">
                    {['Consulting', 'Grant', 'Reimbursement', 'Retainer', 'Other'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={incomeForm.status}
                    onChange={(e) => setIncomeForm({ ...incomeForm, status: e.target.value as any })}
                    className="w-full px-3 py-2 border rounded-md">
                    <option value="received">Received (counts toward revenue)</option>
                    <option value="expected">Expected (not yet counted)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment method</label>
                  <select value={incomeForm.paymentMethod}
                    onChange={(e) => setIncomeForm({ ...incomeForm, paymentMethod: e.target.value as any })}
                    className="w-full px-3 py-2 border rounded-md">
                    {['bank_transfer', 'check', 'card', 'cash', 'other'].map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
                  <select value={incomeForm.projectId}
                    onChange={(e) => setIncomeForm({ ...incomeForm, projectId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md">
                    <option value="">— None —</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client / payer (optional)</label>
                <input type="text"
                  value={incomeForm.clientName}
                  onChange={(e) => setIncomeForm({ ...incomeForm, clientName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowIncomeModal(false)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={savingIncome} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50">
                  {savingIncome ? 'Saving…' : 'Record income'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialLedgerPage;
